import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GitCompareArrows, Trash2, Loader2, Trophy } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmt, euros } from "@/lib/cee";
import { FadeIn } from "@/components/Reveal";

const emptyImg =
  "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop";

const METRICS = (s) => {
  const t = s.totals || {};
  return {
    "Date": (s.created_at || "").slice(0, 10).split("-").reverse().join("/"),
    "Opérations": fmt(t.nbOperations),
    "Fiches": [...new Set((s.operations || []).map((o) => o.fiche))].join(", ") || "—",
    "Puissance totale": `${fmt(t.puissanceTotale, 1)} kW`,
    "MWh cumac": fmt(t.mwhCumacTotal, 1),
    "Valorisation du MWh cumac": `${fmt(t.prixCession, 2)} €/MWh`,
    "Commission apport d'affaires": t.commissionTotal ? euros(Math.round(t.commissionTotal)) : "—",
    "Prime CEE estimée": euros(Math.round(t.primeNette || 0)),
  };
};

const Comparateur = () => {
  const [sims, setSims] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => api.get("/simulations").then(({ data }) => {
    setSims(data);
    setSelected((prev) => (prev.length ? prev : data.slice(0, 4).map((s) => s.id)));
  }).catch((e) => toast.error(apiErrorMessage(e)));
  useEffect(() => { load(); }, []);

  const chosen = useMemo(() => (sims || []).filter((s) => selected.includes(s.id)), [sims, selected]);
  const bestPrime = Math.max(-Infinity, ...chosen.map((s) => s.totals?.primeNette || 0));

  const deleteSim = async (id) => {
    if (!window.confirm("Supprimer cette simulation du comparateur ?")) return;
    await api.delete(`/simulations/${id}`).catch(() => {});
    toast.success("Simulation supprimée.");
    load();
  };

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 4 ? prev : [...prev, id]));

  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-24 sm:px-6">
      <div className="mb-8">
        <p className="mb-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--accent-text)]">Comparateur de simulations</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">
          Comparez vos primes, côte à côte
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-[var(--dim)]">
          Enregistrez plusieurs simulations depuis le calculateur (bouton « Comparer »), puis sélectionnez-en jusqu'à 4 pour comparer
          leurs résultats.
        </p>
      </div>

      {sims === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[var(--accent-text)]" /></div>
      ) : sims.length === 0 ? (
        <FadeIn className="card-surface relative overflow-hidden p-10 text-center">
          <img src={emptyImg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-8" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090D14] via-[#090D14]/85 to-[#090D14]/70" />
          <div className="relative">
            <GitCompareArrows size={38} className="mx-auto mb-4 text-[var(--accent-text)]" />
            <h2 className="font-display text-xl font-bold text-[var(--ink)]">Aucune simulation enregistrée</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-[var(--dim)]">
              Depuis le calculateur, remplissez vos opérations puis cliquez sur « Comparer » pour enregistrer une simulation ici.
            </p>
          </div>
        </FadeIn>
      ) : (
        <>
          {/* Sélection */}
          <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {sims.map((s) => {
              const active = selected.includes(s.id);
              const t = s.totals || {};
              return (
                <motion.div key={s.id} layout data-testid={`sim-card-${s.id}`}
                  className={`relative cursor-pointer rounded-2xl border p-4 transition-all ${
                    active ? "border-sky-500/60 bg-sky-500/8 glow-brand" : "border-[var(--line)] bg-[var(--panel)] hover:border-[var(--line-hover)]"
                  }`}
                  onClick={() => toggle(s.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span data-testid={`sim-name-${s.id}`} className="truncate font-medium text-[var(--ink)]">{s.nom}</span>
                    <button data-testid={`sim-delete-btn-${s.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteSim(s.id); }}
                      className="text-[var(--mute)] transition hover:text-[var(--rose-text)]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="mt-2 font-mono text-xs text-[var(--mute)]">{fmt(t.mwhCumacTotal, 1)} MWh cumac</div>
                  <div className="font-display mt-1 text-xl font-bold text-[var(--accent-text)]">{euros(Math.round(t.primeNette || 0))}</div>
                  {active && <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-[10px] font-bold text-white">✓</span>}
                </motion.div>
              );
            })}
          </div>

          {/* Matrice comparative */}
          {chosen.length > 0 && (
            <FadeIn className="card-surface overflow-x-auto">
              <table data-testid="comparateur-matrix" className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-[var(--line)]">
                    <th className="px-5 py-4 text-left text-[10px] font-mono uppercase tracking-widest text-[var(--mute)]">Critère</th>
                    {chosen.map((s) => (
                      <th key={s.id} className="px-5 py-4 text-left font-display text-sm font-bold text-[var(--ink)]">{s.nom}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(METRICS(chosen[0])).map(([label, _]) => (
                    <tr key={label} className="border-b border-[var(--line-soft)] last:border-0">
                      <td className="px-5 py-3 text-xs uppercase tracking-wider text-[var(--mute)]">{label}</td>
                      {chosen.map((s) => (
                        <td key={s.id} className="px-5 py-3 font-mono text-[var(--dim)]">{METRICS(s)[label]}</td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="px-5 py-4 text-xs uppercase tracking-wider text-[var(--mute)]">Prime nette</td>
                    {chosen.map((s) => {
                      const p = s.totals?.primeNette || 0;
                      const best = p === bestPrime;
                      return (
                        <td key={s.id} data-testid={`sim-prime-${s.id}`} className="px-5 py-4">
                          <span className={`font-display inline-flex items-center gap-2 text-lg font-bold ${best ? "text-[var(--accent-text)]" : "text-[var(--dim)]"}`}>
                            {best && <Trophy size={15} className="text-[var(--amber-text)]" />}
                            {euros(Math.round(p))}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </FadeIn>
          )}
          {chosen.length === 0 && (
            <p className="rounded-2xl border border-dashed border-[var(--line)] px-6 py-10 text-center text-sm text-[var(--mute)]">
              Sélectionnez au moins une simulation ci-dessus pour lancer la comparaison.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Comparateur;
